//------------------------------------------------
//
//	ReU: 画像のパララックス
//	対象は .parallaxCover（管理画面「枠内」＝枠は固定で中の画像が流れる）と .parallaxFloat（「枠ごと」＝箱そのものが動く）。
//	どちらも移動量を CSS 変数 --parallax-y に書くだけで、描き方は reu-parallax.css が決める。
//	出現アニメと同じ要素に付いても消し合わない（CSS 側で足し合わせている）。
//
//	🔑 位置は「ページ先頭からの値」で先に測っておき、スクロール中は測り直さない。
//	   移動量はスクロール量だけで決まる純粋な計算になり、前のフレームの値に影響されない。
//	🔑 Lenis がいるときは Lenis のスクロール通知で更新する（同じフレームで反映される）。
//	   いないときだけ毎フレームのループを回し、画面に1枚も無ければループ自体を止める。
//
//------------------------------------------------
(() => {
    const SELECTOR = '.parallaxCover, .parallaxFloat';
    const ACTIVE_CLASS = 'parallaxOn';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let observer = null;
    let items = [];          // 測り終えた対象。位置はページ先頭からの値
    let visible = new Set(); // 画面に入っているもの＝書き込む対象
    let amount = 0;          // 動く量（枠の高さに対する％）。組み立て時に1回だけ読む
    let frame = null;
    let lenisBound = false;

    //	動く量（枠の高さに対する％）。0ならパララックス無効
    const amountPercent = () => {
        const raw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--parallax-amount'));
        return Number.isNaN(raw) ? 8 : raw;
    };

    //	スマホで止める設定（1なら止める）
    const stopOnMobile = () =>
        getComputedStyle(document.documentElement).getPropertyValue('--parallax-sp-off').trim() === '1';

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

    const scrolled = () => window.scrollY || document.documentElement.scrollTop || 0;

    //	Lenis がスクロールを預かっているか。Lenis は位置を DOM に書いてから scroll を通知するので、
    //	この通知の中で測れば「ページと同じフレームの位置」になる。
    const usingLenis = () => !!(window.lenis && typeof window.lenis.on === 'function');

    const clear = (element) => {
        element.style.removeProperty('--parallax-y');
    };

    //	自分と祖先に掛かっている縦の移動量の合計。
    //	🔑 出現アニメは transform で要素を下げているので、これを引かないと
    //	   「まだ出ていない状態の位置」を覚えてしまう（最大で出現距離ぶんズレる）。
    const shiftedBy = (element) => {
        let total = 0;
        let node = element;
        while (node && node.nodeType === 1 && node !== document.documentElement) {
            const t = getComputedStyle(node).transform;
            if (t && t !== 'none') total += new DOMMatrixReadOnly(t).m42;
            node = node.parentElement;
        }
        return total;
    };

    //	位置を1回だけ測る。
    //	⚠️ getBoundingClientRect() は「動かした後の位置」を返す。動かしたまま測ると
    //	   次の値が前の値に依存し、フレームごとに揺れる（ガタツキの主因だった）。
    //	   組み立て時に自分の移動量を消したうえで、残る変形分も引いて素の位置を得る。
    const measure = (element) => {
        const rect = element.getBoundingClientRect();
        const isCover = element.classList.contains('parallaxCover');
        //	枠の高さを基準にする（.parallaxCover は自分が枠より高いので親を見る）
        const base = isCover && element.parentElement
            ? element.parentElement.getBoundingClientRect().height
            : rect.height;
        return {
            element,
            isCover,
            top: rect.top + scrolled() - shiftedBy(element), // ページ先頭からの位置（変形なし）
            height: rect.height,
            base,
        };
    };

    const apply = () => {
        if (!visible.size) return;
        const view = window.innerHeight;
        const offset = scrolled();
        items.forEach((item) => {
            if (!visible.has(item.element)) return;
            const span = view + item.height;
            if (span <= 0) return;
            //	画面のどこにいるか。入ってきた瞬間が -1、出ていく瞬間が +1
            const center = item.top - offset + item.height / 2;
            const p = Math.min(Math.max((view / 2 - center) / (span / 2), -1), 1);
            const shift = (item.base * amount) / 100;
            //	枠内＝枠の中で上下対称に動かす。枠は動かないので位置のズレは起きない。
            //	枠ごと＝画面に入った時点を「元の位置」にして、そこから下へ動く。
            //	  ⚠️ ここを対称にすると、入ってきた瞬間に箱が「動く量」ぶんズレて置かれる。
            const y = item.isCover ? p * shift : ((p + 1) / 2) * shift;
            item.element.style.setProperty('--parallax-y', `${y.toFixed(3)}px`);
        });
    };

    //	Lenis が無いときだけ。画面に画像がある間だけ回し、無くなったら止める
    const tick = () => {
        apply();
        frame = visible.size ? requestAnimationFrame(tick) : null;
    };

    const start = () => {
        if (usingLenis()) return;
        if (frame === null && visible.size) frame = requestAnimationFrame(tick);
    };

    const bindLenis = () => {
        if (lenisBound || !usingLenis()) return;
        window.lenis.on('scroll', apply);
        lenisBound = true;
    };

    const build = () => {
        if (observer) observer.disconnect();
        visible.clear();
        items = [];
        if (frame !== null) {
            cancelAnimationFrame(frame);
            frame = null;
        }

        const targets = Array.from(document.querySelectorAll(SELECTOR));
        document.documentElement.classList.remove(ACTIVE_CLASS);
        targets.forEach(clear);
        if (!targets.length) return;

        //	スマホで止める設定なら、位置だけ戻して何もしない。
        //	🔑 印（html.parallaxOn）を外すことで CSS 側の指定も解除される
        //	   ＝動かさないのに画像だけ変形されている、という状態を作らない。
        if (stopOnMobile() && isMobile()) return;
        document.documentElement.classList.add(ACTIVE_CLASS);

        amount = amountPercent();
        items = targets.map(measure);

        //	少し手前から見はじめる（画面に入る直前に位置を合わせておく）
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        visible.add(entry.target);
                    } else {
                        visible.delete(entry.target);
                        clear(entry.target);
                    }
                });
                apply();
                start();
            },
            { rootMargin: '10% 0px' }
        );

        targets.forEach((element) => observer.observe(element));
        bindLenis();
    };

    window.reuParallax = {
        state: () => ({
            amount,
            driver: usingLenis() ? 'lenis' : 'raf',
            targets: document.querySelectorAll(SELECTOR).length,
            watching: visible.size,
            running: frame !== null,
        }),
        rebuild: () => build(),
    };

    const init = () => {
        build();
        window.addEventListener('load', () => build(), { once: true });

        let timer = null;
        const onResize = () => {
            clearTimeout(timer);
            timer = setTimeout(build, 200);
        };
        window.addEventListener('resize', onResize);
        if (window.ResizeObserver) {
            new ResizeObserver(onResize).observe(document.documentElement);
        }

        if (window.Shopify && Shopify.designMode) {
            document.addEventListener('shopify:section:load', build);
            document.addEventListener('shopify:section:reorder', build);
        }
    };

    //	🔑 Lenis は reu-scroll.js が DOMContentLoaded で作る。defer 実行の時点では
    //	   readyState が 'interactive' でまだ存在しないため、必ず DOMContentLoaded を待つ。
    //	   （動的に読み込まれて DOMContentLoaded を取り逃した場合の保険に load も見る）
    let booted = false;
    const boot = () => {
        if (booted) return;
        booted = true;
        init();
    };

    if (document.readyState === 'complete') {
        boot();
    } else {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
        window.addEventListener('load', boot, { once: true });
    }
})();
